import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Simple message component to avoid toast serialization issues
const AuthMessage = ({ type, message }) => {
  if (!message) return null;
  
  return (
    <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-lg flex items-center gap-2 ${
      type === 'error' ? 'bg-red-500/20 border border-red-500/50 text-red-300' : 
      type === 'success' ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-300' :
      'bg-cyan-500/20 border border-cyan-500/50 text-cyan-300'
    }`}>
      {type === 'error' && <XCircle className="w-5 h-5" />}
      {type === 'success' && <CheckCircle className="w-5 h-5" />}
      <span>{message}</span>
    </div>
  );
};

export const AuthCallback = () => {
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState({ type: null, message: null });

  useEffect(() => {
    // Use useRef to prevent StrictMode double execution
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          setStatus({ type: 'error', message: 'No session ID found' });
          setTimeout(() => navigate('/login', { replace: true }), 1500);
          return;
        }

        // Exchange session_id for local session
        const data = await exchangeSession(sessionId);
        
        if (data.user) {
          setStatus({ type: 'success', message: 'Inicio de sesión exitoso' });
          // Clear the hash from URL before navigating
          window.history.replaceState(null, '', window.location.pathname);
          setTimeout(() => navigate('/panel', { replace: true, state: { user: data.user } }), 500);
        } else {
          throw new Error('No user data received');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus({ type: 'error', message: 'Error al procesar la autenticación' });
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      }
    };

    processAuth();
  }, [exchangeSession, navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <AuthMessage type={status.type} message={status.message} />
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
        <p className="text-slate-400">Procesando autenticación...</p>
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If loading, wait
    if (loading) return;

    // If user data passed from AuthCallback, we're authenticated
    if (location.state?.user) return;

    // If not authenticated after loading, redirect
    if (!isAuthenticated) {
      navigate('/login', { replace: true, state: { from: location.pathname } });
    }
  }, [isAuthenticated, loading, navigate, location]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
      </div>
    );
  }

  // If authenticated or has user from callback, render children
  if (isAuthenticated || location.state?.user) {
    return children;
  }

  return null;
};

export const SubscriptionRoute = ({ children }) => {
  const { hasSubscription, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;

    if (!isAuthenticated) {
      navigate('/login', { replace: true, state: { from: location.pathname } });
      return;
    }

    if (!hasSubscription) {
      navigate('/pricing', { replace: true });
      // Removed toast to avoid serialization issues
    }
  }, [hasSubscription, loading, isAuthenticated, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (isAuthenticated && hasSubscription) {
    return children;
  }

  return null;
};
